import { createContext, PropsWithChildren, useCallback, useMemo } from 'react'
import gsap from 'gsap'
import { ScrollToOptions } from 'lenis'
import ReactLenis, { useLenis } from 'lenis/react'

import Controller from '@/contexts/lenis/components/controller'
import Scrollbar from '@/contexts/lenis/components/scrollbar'

export type Context = {
  scrollTo: (
    _target: number | string | HTMLElement,
    _options?: ScrollToOptions
  ) => void
}

export const LenisContext = createContext<Context | undefined>(undefined)

const LenisProvider = ({ children }: PropsWithChildren) => {
  const lenis = useLenis()

  useMemo(() => {
    if (lenis) {
      lenis.options.lerp = 0.1
      lenis.options.duration = 1.7
    }
  }, [lenis])

  const scrollTo = useCallback(
    (target: number | string | HTMLElement, options?: ScrollToOptions) => {
      lenis?.scrollTo(target, {
        duration: 2.5,
        easing: gsap.parseEase('expo.inOut'),
        ...options
      })
    },
    [lenis]
  )

  const contextValue = useMemo(() => ({ scrollTo }), [scrollTo])

  return (
    <LenisContext.Provider value={contextValue}>
      <Controller />
      {children}
      <Scrollbar />
    </LenisContext.Provider>
  )
}

const LenisRoot = ({ children }: PropsWithChildren) => {
  return (
    <ReactLenis
      root
      options={{
        prevent: (node) => node.classList.contains('pm__body')
      }}
    >
      <LenisProvider>{children}</LenisProvider>
    </ReactLenis>
  )
}

export default LenisRoot
